import { IAbstractInitialState, TLoading } from "../../types/common";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { TNews } from "../../types/newsCard";
import {
  getNewsPagination,
  newsCard,
  newsCards,
} from "../../api/asyncThuncks/newsCards";

interface IInitialState extends IAbstractInitialState {
  news: TNews[];
  newsById: TNews;
  youtubeUrl: string;
  total: number | null;
  page: number;
  paginationLoading: TLoading;
  paginationError: string;
  paginationStarted: boolean;
  paginationRequestId?: string;
}

const initialState: IInitialState = {
  loading: "idle",
  news: [] as TNews[],
  newsById: {
    id: "",
    title: "",
    description: "",
    imagePath: "",
    createdAt: "",
    publicUrl: "",
    viewCounter: "",
    isPublic: false,
    youtubeUrl: "",
  },
  youtubeUrl: "",
  total: null,
  page: 0,
  paginationLoading: "idle",
  paginationError: "",
  paginationStarted: false,
  error: "",
};

const newsSlice = createSlice({
  name: "newsSlice",
  initialState,
  reducers: {
    setYoutubeUrlState(state, { payload }: PayloadAction<string>) {
      state.youtubeUrl = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(newsCards.pending, (state) => {
        if (!state.paginationStarted) state.loading = "pending";
      })
      .addCase(newsCards.fulfilled, (state, { payload }) => {
        if (!state.paginationStarted) {
          state.news = [...payload];
          state.loading = "idle";
        }
      })
      .addCase(newsCards.rejected, (state, { error }) => {
        if (!state.paginationStarted) {
          if (error.message) state.error = error.message;
          state.loading = "failed";
        }
      })
      .addCase(getNewsPagination.pending, (state, { meta }) => {
        state.paginationStarted = true;
        state.paginationLoading = "pending";
        state.paginationError = "";
        state.paginationRequestId = meta.requestId;
        if (meta.arg.page === 1) {
          state.news = [];
          state.total = null;
          state.page = 0;
        }
      })
      .addCase(getNewsPagination.fulfilled, (state, { payload, meta }) => {
        if (state.paginationRequestId !== meta.requestId) return;
        const existingIds = new Set(
          meta.arg.page === 1 ? [] : state.news.map((card) => card.id)
        );
        const nextCards = payload.data.filter((card) => {
          if (existingIds.has(card.id)) return false;
          existingIds.add(card.id);
          return true;
        });
        state.news = meta.arg.page === 1 ? nextCards : [...state.news, ...nextCards];
        state.total = payload.total;
        state.page = meta.arg.page;
        state.paginationLoading = "idle";
        state.paginationError = "";
        state.paginationRequestId = undefined;
      })
      .addCase(getNewsPagination.rejected, (state, { error, meta }) => {
        if (state.paginationRequestId !== meta.requestId) return;
        state.paginationError = meta.aborted ? "" : error.message || "Request failed";
        state.paginationLoading = meta.aborted ? "idle" : "failed";
        state.paginationRequestId = undefined;
      })
      .addCase(newsCard.pending, (state) => {
        state.loading = "pending";
        state.error = "";
      })
      .addCase(newsCard.fulfilled, (state, { payload }) => {
        state.loading = "idle";
        state.error = "";
        state.newsById = { ...payload };
      })
      .addCase(newsCard.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      });
  },
});

export const selectNews = (state: RootState): IInitialState => state.news;

export const { setYoutubeUrlState } = newsSlice.actions;

export default newsSlice.reducer;
